import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group relative block overflow-hidden rounded-lg bg-background-50"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={comic.coverImage}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - ${comic.author}作品`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500 text-white whitespace-nowrap">
              {comic.status}
            </span>
            <span className="text-xs text-white/80">
              <i className="ri-star-fill text-accent-500 mr-1"></i>
              {comic.rating}
            </span>
            <span className="text-xs text-white/80">
              <i className="ri-eye-line mr-1"></i>
              {(comic.views / 10000).toFixed(1)}万
            </span>
          </div>
          <h3 className="text-lg font-bold text-white mb-1">{comic.title}</h3>
          <p className="text-sm text-white/70 line-clamp-2">{comic.description}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.coverImage}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - ${comic.author}作品`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-accent-600">
              <i className="ri-star-fill text-xs mr-0.5"></i>
              {comic.rating}
            </span>
            <span className="text-xs text-foreground-400">{comic.category}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300"
      data-product-shop=""
    >
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - ${comic.author}作品`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          {comic.isHot && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-primary-500 text-white whitespace-nowrap">
              热门
            </span>
          )}
          {comic.isNew && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-accent-500 text-white whitespace-nowrap">
              新作
            </span>
          )}
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="flex items-center text-xs text-accent-600">
            <i className="ri-star-fill text-xs mr-0.5"></i>
            {comic.rating}
          </span>
          <span className="text-[11px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
            {comic.category}
          </span>
        </div>
      </div>
    </Link>
  );
}